import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Heart, Phone, User, Shield, Check, X, AlertCircle } from 'lucide-react'
import { authApi, settingsApi } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { usePageTitle } from '../../hooks/usePageTitle'

const mockWechatUser = {
  nickname: '健康达人',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=wxuser',
  openid: 'wx_demo_openid_001',
}

const UserLogin = () => {
  const navigate = useNavigate()
  const { login: authLogin } = useAuth()
  usePageTitle()

  const [wechatLoginEnabled, setWechatLoginEnabled] = useState(false)
  // 是否为小程序环境（仅在小程序环境下才展示微信一键登录按钮）
  const isMiniProgram = /miniProgram/i.test(navigator.userAgent) || window.__wxjs_environment === 'miniprogram'
  const [loginMode, setLoginMode] = useState<'sms' | 'password'>('sms')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [showWechatModal, setShowWechatModal] = useState(false)
  const [wechatLoading, setWechatLoading] = useState(false)
  const [isFirstLogin, setIsFirstLogin] = useState(false)
  const [showCommitment, setShowCommitment] = useState(false)
  const [commitmentSigned, setCommitmentSigned] = useState(false)
  const [showAgreementModal, setShowAgreementModal] = useState(false)
  const [agreementType, setAgreementType] = useState<'user' | 'privacy'>('user')
  const [error, setError] = useState('')

  const [smsCode, setSmsCode] = useState('')
  const [smsCountdown, setSmsCountdown] = useState(0)
  const [smsStep, setSmsStep] = useState<'phone' | 'code'>('phone')

  const [showBindModal, setShowBindModal] = useState(false)
  const [bindMode, setBindMode] = useState<'quick' | 'sms'>('quick')
  const [pendingWechatUser, setPendingWechatUser] = useState<typeof mockWechatUser | null>(null)
  const [wechatPhone, setWechatPhone] = useState('')
  const [smsPhone, setSmsPhone] = useState('')
  const [phoneLoading, setPhoneLoading] = useState(false)
  const [commitmentContent, setCommitmentContent] = useState('')
  const [userAgreementContent, setUserAgreementContent] = useState('')
  const [privacyPolicyContent, setPrivacyPolicyContent] = useState('')

  const finishLogin = useCallback(async (resp: { token: string; account: any }, wechatInfo?: typeof mockWechatUser | null) => {
    const { account } = resp

    // 调用 AuthProvider.login() 设置全局认证状态
    authLogin(resp)

    // 兼容旧版存储（部分页面仍使用）
    localStorage.setItem('userLoggedIn', 'true')
    localStorage.setItem('userAccount', JSON.stringify({
      id: account.id, name: account.name, phone: account.phone, avatar: account.avatar,
      role: account.role, medicalRole: account.medical_role, hospital: account.hospital_name,
      points: account.points, idCard: account.id_card,
    }))
    if (account.role === 'medical') {
      localStorage.setItem('medicalLoggedIn', 'true')
      localStorage.setItem('medicalUser', JSON.stringify({
        id: account.id, name: account.name, phone: account.phone,
        hospital: account.hospital_name, role: resp.account.medical_role,
        idCard: account.id_card, avatar: account.avatar,
      }))
    }
    if (wechatInfo) localStorage.setItem('wechatUser', JSON.stringify(wechatInfo))
    
    // 检查是否需要签署承诺书
    try {
      const commitmentConfig = await settingsApi.getCommitment()
      if (commitmentConfig.enabled) {
        // 检查用户是否已签署
        const checkResult = await settingsApi.checkSigned(account.id)
        if (!checkResult.signed) {
          // 未签署，设置承诺书内容并显示弹窗
          if (commitmentConfig.content) {
            setCommitmentContent(commitmentConfig.content)
          }
          setShowCommitment(true)
          return
        }
      }
    } catch (e) {
      console.error('检查承诺书状态失败:', e)
    }
    
    // 已签署或不需要签署，直接跳转
    navigate('/user/profile')
  }, [authLogin, navigate])

  const handlePasswordLogin = async () => {
    setError('')
    if (!phone || !password) { setError('请填写手机号和密码'); return }
    if (!agreed) { setError('请同意用户协议'); return }
    setLoading(true)
    try {
      const resp = await authApi.login(phone, password)
      if (resp.account.role === 'user' || resp.account.role === 'medical') {
        finishLogin(resp)
      } else { setError('此账号不能用于用户端登录') }
    } catch (e) { setError((e as Error).message || '登录失败') }
    finally { setLoading(false) }
  }

  const handleSmsSendCode = async () => {
    setError('')
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) { setError('请输入正确的手机号'); return }
    if (!agreed) { setError('请同意用户协议'); return }
    try {
      await authApi.sendCode(phone)
      setSmsStep('code'); setSmsCountdown(60)
      const timer = setInterval(() => { setSmsCountdown(prev => { if (prev <= 1) { clearInterval(timer); return 0 } return prev - 1 }) }, 1000)
    } catch (e) { setError((e as Error).message) }
  }

  const handleSmsLogin = async () => {
    setError('')
    if (!smsCode || smsCode.length !== 6) { setError('请输入6位验证码'); return }
    setLoading(true)
    try {
      // 后端自动判断：手机号已注册则登录，未注册则创建
      const resp = await authApi.register({ phone, password: 'Ms' + phone.slice(-6), name: `用户${phone.slice(-4)}`, code: smsCode })
      finishLogin(resp)
    } catch (e: any) {
      setError((e as Error).message || '验证失败')
      setSmsStep('phone')
    } finally { setLoading(false) }
  }

  const handleWechatLogin = () => {
    // 兼容：优先用 wx.miniProgram.navigateTo（直接跳转），fallback 到 postMessage
    const isMiniProgram = /miniProgram/i.test(navigator.userAgent) || window.__wxjs_environment === 'miniprogram'
    if (isMiniProgram && window.wx) {
      // 方式1：直接跳转（推荐，文档明确说明无需签名）
      try {
        window.wx.miniProgram.navigateTo({ url: '/pages/login/index' })
        return
      } catch (e) {
        console.warn('navigateTo 失败，尝试 postMessage', e)
      }
      // 方式2：通过 postMessage（部分版本需要）
      window.wx.miniProgram.postMessage({ type: 'navigate', url: '/pages/login/index' })
    } else {
      setShowWechatModal(true)
    }
  }

  // 小程序登录页返回后，H5 通过 URL 参数接收 token 并自动登录
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (token) {
      ;(async () => {
        try {
          const resp = await fetch(`${import.meta.env.VITE_API_BASE_URL}/users/me`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (resp.ok) {
            const userData = await resp.json()
            finishLogin({ token, account: userData })
            // 清理 URL 参数（不刷新页面）
            const cleanUrl = window.location.origin + window.location.pathname
            window.history.replaceState({}, '', cleanUrl)
          }
        } catch (e) {
          console.error('小程序登录回调失败', e)
        }
      })()
    }
  }, [finishLogin])

  const handleWechatAuth = async () => {
    setWechatLoading(true); setError('')
    try {
      const resp = await authApi.wechatLogin('placeholder_wx_code')
      setWechatLoading(false); setShowWechatModal(false); finishLogin(resp)
    } catch (e) { setWechatLoading(false); setError((e as Error).message || '微信登录失败') }
  }

  const handleBindSmsSendCode = async () => {
    setError('')
    if (!smsPhone || !/^1[3-9]\d{9}$/.test(smsPhone)) { setError('请输入正确的手机号'); return }
    try {
      await authApi.sendCode(smsPhone)
      setSmsCountdown(60)
      const timer = setInterval(() => { setSmsCountdown(prev => { if (prev <= 1) { clearInterval(timer); return 0 } return prev - 1 }) }, 1000)
      setSmsStep('code')
    } catch (e) { setError((e as Error).message) }
  }

  const handleSmsVerifyAndBind = async () => {
    setError('')
    if (!smsCode || smsCode.length !== 6) { setError('请输入6位验证码'); return }
    setLoading(true)
    try {
      const resp = await authApi.register({ phone: smsPhone, password: 'Ms' + smsPhone.slice(-6), name: `用户${smsPhone.slice(-4)}`, id_card: '', code: smsCode })
      setLoading(false); setShowBindModal(false); finishLogin(resp)
    } catch (e: any) { setLoading(false); setError((e as Error).message || '验证失败') }
  }

  const handleSignCommitment = async () => {
    if (!commitmentSigned) { setError('请先阅读并同意《健康生活承诺书》'); return }
    setLoading(true)
    try {
      // 调用后端 API 记录签署
      await settingsApi.signCommitment()
      setShowCommitment(false)
      navigate('/user/profile')
    } catch (e) {
      setError((e as Error).message || '签署失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  const systemLogo = localStorage.getItem('systemLogo')

  // 加载微信登录配置和协议内容
  useEffect(() => {
    const loadWechatSetting = async () => {
      try {
        const data = await settingsApi.getSystem()
        if (data && typeof data.wechatLoginEnabled === 'boolean') {
          setWechatLoginEnabled(data.wechatLoginEnabled)
        }
      } catch (e) {
        console.error('加载微信登录配置失败', e)
      }
    }
    const loadAgreements = async () => {
      try {
        const data = await settingsApi.getAgreements()
        setUserAgreementContent(data.userAgreement || '')
        setPrivacyPolicyContent(data.privacyPolicy || '')
      } catch (e) {
        console.error('加载协议内容失败', e)
      }
    }
    loadWechatSetting()
    loadAgreements()
  }, [])

  return (
    <>
    <div className="bg-gradient-to-b from-green-700 to-green-600 flex flex-col items-center px-6 pt-8 pb-4">
      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg mb-4 overflow-hidden">
        {systemLogo ? (
          <img src={systemLogo} alt="logo" className="w-full h-full object-contain" />
        ) : (
          <Heart className="w-10 h-10 text-green-600" />
        )}
      </div>
      <h1 className="text-2xl font-bold text-white">{localStorage.getItem('systemTitle') || '健康积分'}</h1>
      <p className="text-green-200 mt-1 text-sm">健康生活，从每一次参与开始</p>
    </div>
    <div className="bg-white rounded-t-3xl px-6 py-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">注册登录</h2>

        {error && (
          <div className="mb-4 p-3 bg-gradient-to-r from-red-50 to-rose-50 border border-red-300 rounded-xl flex items-center shadow-sm animate-shake">
            <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-red-600" strokeWidth={2.4} />
            </div>
            <span className="text-red-700 text-sm font-medium flex-1">{error}</span>
            <button
              onClick={() => setError('')}
              className="ml-2 p-1 hover:bg-red-100 rounded transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4 text-red-400" />
            </button>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">手机号</label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="tel" placeholder="请输入手机号" value={phone} onChange={(e) => setPhone(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
          </div>
        </div>

        {loginMode === 'sms' && (
          <>
            {smsStep === 'code' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">验证码</label>
                <input type="text" placeholder="请输入6位验证码" value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value)} maxLength={6}
                  className="w-full pl-4 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-center tracking-widest text-lg" />
              </div>
            )}
            <div className="flex items-center mb-6">
              <label className="flex items-center flex-wrap">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                  className="w-4 h-4 text-green-600 rounded focus:ring-green-500" />
                <span className="ml-2 text-sm text-gray-600">同意</span>
                <button type="button" onClick={() => { setAgreementType('user'); setShowAgreementModal(true) }}
                  className="text-green-600 text-sm ml-1">《用户协议》</button>
                <span className="text-sm text-gray-600 mx-1">和</span>
                <button type="button" onClick={() => { setAgreementType('privacy'); setShowAgreementModal(true) }}
                  className="text-green-600 text-sm">《隐私协议》</button>
              </label>
            </div>
            {smsStep === 'phone' ? (
              <button onClick={handleSmsSendCode} disabled={smsCountdown > 0}
                className={`w-full py-4 rounded-xl font-medium transition-colors ${smsCountdown > 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800'}`}>
                {smsCountdown > 0 ? `${smsCountdown}秒后重发` : '获取验证码'}
              </button>
            ) : (
              <button onClick={handleSmsLogin} disabled={loading}
                className={`w-full py-4 rounded-xl font-medium transition-colors flex items-center justify-center ${loading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800'}`}>
                {loading ? '登录中...' : '登录'}
              </button>
            )}
            {smsStep === 'code' && (
              <button onClick={() => { setSmsStep('phone'); setSmsCode('') }} disabled={smsCountdown > 0}
                className="w-full py-2 mt-2 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50">
                {smsCountdown > 0 ? `重新获取验证码（${smsCountdown}秒）` : '收不到验证码？重新获取'}
              </button>
            )}
          </>
        )}

        {loginMode === 'password' && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">密码</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} placeholder="请输入密码"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handlePasswordLogin()}
                  className="w-full pl-4 pr-12 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="flex items-center mb-6">
              <label className="flex items-center flex-wrap">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                  className="w-4 h-4 text-green-600 rounded focus:ring-green-500" />
                <span className="ml-2 text-sm text-gray-600">同意</span>
                <button type="button" onClick={() => { setAgreementType('user'); setShowAgreementModal(true) }}
                  className="text-green-600 text-sm ml-1">《用户协议》</button>
                <span className="text-sm text-gray-600 mx-1">和</span>
                <button type="button" onClick={() => { setAgreementType('privacy'); setShowAgreementModal(true) }}
                  className="text-green-600 text-sm">《隐私协议》</button>
              </label>
            </div>
            <button onClick={handlePasswordLogin} disabled={loading}
              className={`w-full py-4 rounded-xl font-medium transition-colors flex items-center justify-center ${loading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800'}`}>
              {loading ? '登录中...' : '登录'}
            </button>
          </>
        )}

        <button type="button" onClick={() => { setLoginMode(loginMode === 'sms' ? 'password' : 'sms'); setError(''); setSmsStep('phone'); setSmsCode('') }}
          className="w-full py-2 mt-3 text-sm text-green-600 hover:underline">
          {loginMode === 'sms' ? '密码登录' : '短信验证码登录'}
        </button>

        {wechatLoginEnabled && isMiniProgram && (
          <>
            <div className="flex items-center my-6">
              <div className="flex-1 border-t border-gray-200" />
              <span className="px-4 text-sm text-gray-400">或</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>
            <button onClick={handleWechatLogin}
              className="w-full py-4 bg-green-50 text-green-700 border border-green-200 rounded-xl font-medium hover:bg-green-100 transition-colors flex items-center justify-center space-x-2">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348z"/>
              </svg>
              <span>微信一键登录</span>
            </button>
          </>
        )}
      </div>

      {/* 底部版权和备案号 */}
      <div className="px-4 pb-6 pt-4 text-center">
        <p className="text-xs text-gray-400">{localStorage.getItem('copyright') || '© 2026 眉山疾控'}</p>
        {localStorage.getItem('icpNumber') ? (
          <p className="text-xs text-gray-400 mt-1">{localStorage.getItem('icpNumber')}</p>
        ) : null}
      </div>

      {/* 微信授权弹窗 */}
      {showWechatModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center">
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348z"/></svg>
                微信授权登录
              </h3>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  {wechatLoading ? <div className="w-10 h-10 border-4 border-green-600 border-t-transparent rounded-full animate-spin" /> : <User className="w-10 h-10 text-gray-400" />}
                </div>
                <p className="text-gray-600 text-sm">{wechatLoading ? '正在获取微信授权...' : '点击下方按钮获取微信授权'}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-gray-500 mb-2">授权后将自动获取：</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />微信昵称</li>
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />微信头像</li>
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />唯一标识(OpenID)</li>
                </ul>
              </div>
              <div className="flex items-start space-x-3 mb-4">
                <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-500">我们仅获取必要信息用于账号绑定，您的个人隐私将受到严格保护</p>
              </div>
              <button onClick={handleWechatAuth} disabled={wechatLoading}
                className={`w-full py-3 rounded-xl font-medium transition-colors ${wechatLoading ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800'}`}>
                {wechatLoading ? '授权中...' : '确认授权'}
              </button>
              <button onClick={() => setShowWechatModal(false)} className="w-full py-2 mt-2 text-gray-500 text-sm hover:text-gray-700">取消</button>
            </div>
          </div>
        </div>
      )}

      {/* 承诺书弹窗 */}
      {showCommitment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex-shrink-0">
              <h3 className="text-lg font-bold text-white flex items-center"><Heart className="w-6 h-6 mr-2" />健康生活承诺书</h3>
              <p className="text-green-100 text-sm mt-1">请仔细阅读并签署承诺书</p>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 space-y-3 max-h-60 overflow-y-auto whitespace-pre-wrap">
                {commitmentContent || '加载中...'}
                <p className="text-gray-500 text-xs mt-4">签署日期：{new Date().toLocaleDateString('zh-CN')}</p>
              </div>
              <label className="flex items-start mt-4 cursor-pointer">
                <input type="checkbox" checked={commitmentSigned} onChange={(e) => setCommitmentSigned(e.target.checked)}
                  className="w-5 h-5 mt-0.5 text-green-600 rounded focus:ring-green-500" />
                <span className="ml-3 text-sm text-gray-700">我已阅读并同意《健康生活承诺书》中的所有条款</span>
              </label>
            </div>
            <div className="p-6 border-t flex-shrink-0">
              <button onClick={handleSignCommitment}
                className={`w-full py-3 rounded-xl font-medium transition-colors ${commitmentSigned ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                签署承诺书
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 协议弹窗 */}
      {showAgreementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-bold text-white">{agreementType === 'user' ? '用户协议' : '隐私协议'}</h3>
              <button onClick={() => setShowAgreementModal(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5 text-white" /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {agreementType === 'user' ? (
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{userAgreementContent || '加载中...'}</div>
              ) : (
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{privacyPolicyContent || '加载中...'}</div>
              )}
            </div>
            <div className="p-6 border-t flex-shrink-0">
              <button onClick={() => setShowAgreementModal(false)}
                className="w-full py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors">我已阅读</button>
            </div>
          </div>
        </div>
      )}

      {/* 微信绑定弹窗（简化版，保留核心绑定逻辑） */}
      {showBindModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348z"/></svg>
                  绑定微信账号
                </h3>
                <button onClick={() => { setShowBindModal(false); setError('') }} className="p-1 text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-green-100 text-sm mt-1">微信授权成功，请选择绑定方式</p>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-4 text-center">
                <p className="text-sm text-gray-600">将微信账号绑定到您的健康积分账号</p>
              </div>
              {bindMode === 'sms' && (
                <div className="space-y-3">
                  <input type="tel" placeholder="请输入手机号" value={smsPhone} onChange={(e) => setSmsPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                  {smsStep === 'code' && (
                    <input type="text" placeholder="请输入6位验证码" value={smsCode} onChange={(e) => setSmsCode(e.target.value)} maxLength={6}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-center tracking-widest" />
                  )}
                  {error && <div className="flex items-center text-red-600 text-sm"><X className="w-4 h-4 mr-1" />{error}</div>}
                  {smsStep === 'phone' ? (
                    <button onClick={handleBindSmsSendCode} disabled={smsCountdown > 0}
                      className={`w-full py-3 rounded-xl font-medium ${smsCountdown > 0 ? 'bg-gray-100 text-gray-400' : 'bg-green-700 text-white hover:bg-green-800'}`}>
                      {smsCountdown > 0 ? `${smsCountdown}秒后重发` : '获取验证码'}
                    </button>
                  ) : (
                    <button onClick={handleSmsVerifyAndBind} disabled={loading}
                      className={`w-full py-3 rounded-xl font-medium flex items-center justify-center ${loading ? 'bg-gray-300 text-gray-500' : 'bg-green-700 text-white hover:bg-green-800'}`}>
                      {loading ? '绑定中...' : '确认绑定'}
                    </button>
                  )}
                </div>
              )}
              <button onClick={() => setShowBindModal(false)} className="w-full py-2 mt-3 text-gray-500 text-sm hover:text-gray-700">取消</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default UserLogin
